import type { components } from "../api/contract";
import { track } from "../telemetry/emit";
import { portalReleaseId } from "./identity";

export type Release = components["schemas"]["Release"];
export type ReleaseSeverity = Release["severity"];
export type ReleaseDetectionSource = "header" | "poll" | "chunk_error";
export type ReleaseTrigger = "manual" | "countdown" | "chunk_error";
export type ReleaseUpdatePhase = "idle" | "available" | "pending" | "reloading";

export type ReleaseUpdateEvent =
  | {
      name: "release.update_detected";
      severity: ReleaseSeverity;
      source: ReleaseDetectionSource;
      ageSeconds: number;
    }
  | {
      name: "release.update_applied";
      severity: ReleaseSeverity;
      trigger: ReleaseTrigger;
      waitedSeconds: number;
    };

export interface PostReloadConfirmation {
  message: "Updated to the latest version.";
  releaseId: string | null;
}

interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ReleaseUpdateRuntime {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
  sessionStorage: SessionStorageLike | null;
  reload: () => void;
}

export interface ReleaseUpdateOptions {
  localWebRelease: string;
  countdownMs?: number;
  snoozeMs?: number;
  runtime?: Partial<ReleaseUpdateRuntime>;
  onEvent?: (event: ReleaseUpdateEvent) => void;
}

export interface ReleaseUpdates {
  readonly phase: ReleaseUpdatePhase;
  readonly release: Release | null;
  readonly severity: ReleaseSeverity | null;
  readonly source: ReleaseDetectionSource | null;
  readonly countdownSeconds: number | null;
  readonly holdReasons: readonly string[];
  readonly held: boolean;
  readonly snoozedUntil: number | null;
  readonly trigger: ReleaseTrigger | null;
  observe: (release: Release, source: ReleaseDetectionSource) => boolean;
  snooze: () => boolean;
  requestReload: (trigger?: ReleaseTrigger) => boolean;
  hold: (reason: string) => () => void;
  claimChunkReload: () => boolean;
  markPostReload: () => void;
  consumePostReloadConfirmation: () => PostReloadConfirmation | null;
  dispose: () => void;
}

export const RELEASE_SNOOZE_MS = 30 * 60 * 1000;
export const REQUIRED_COUNTDOWN_MS = 60 * 1000;
export const CHUNK_RELOAD_GUARD_KEY = "epidev.release.chunkReloaded";
export const POST_RELOAD_KEY = "epidev.release.updated";

const RELEASE_ID_PATTERN = /^(\d{8})T(\d{4})Z-(.+)$/;
const SEVERITY_RANK: Record<ReleaseSeverity, number> = {
  silent: 0,
  notify: 1,
  required: 2
};

function defaultRuntime(): ReleaseUpdateRuntime {
  const browser = typeof window === "undefined" ? null : window;
  let sessionStorage: SessionStorageLike | null = null;
  try {
    sessionStorage = browser?.sessionStorage ?? null;
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  return {
    now: () => Date.now(),
    setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
    clearTimeout: (timer) => clearTimeout(timer),
    sessionStorage,
    reload: () => browser?.location.reload()
  };
}

function releaseTimestamp(value: string): number | null {
  const match = RELEASE_ID_PATTERN.exec(value);
  if (!match) return null;
  const date = match[1];
  const time = match[2];
  if (!date || !time) return null;
  const iso = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${time.slice(0, 2)}:${time.slice(2, 4)}:00.000Z`;
  const parsed = new Date(iso);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString() === iso
    ? parsed.getTime()
    : null;
}

/** Orders canonical release timestamps; equal-minute opaque suffixes do not imply chronology. */
export function compareReleaseIds(left: string, right: string): -1 | 0 | 1 {
  if (left === right) return 0;
  const leftTimestamp = releaseTimestamp(left);
  const rightTimestamp = releaseTimestamp(right);
  if (leftTimestamp !== null && rightTimestamp !== null) {
    if (leftTimestamp === rightTimestamp) return 0;
    return leftTimestamp < rightTimestamp ? -1 : 1;
  }
  return left < right ? -1 : 1;
}

/** `publishedAt` provides the sub-minute ordering absent from canonical release ids. */
export function compareReleases(left: Release, right: Release): -1 | 0 | 1 {
  const idOrder = compareReleaseIds(left.releaseId, right.releaseId);
  if (idOrder !== 0 || left.releaseId === right.releaseId) return idOrder;
  const leftPublishedAt = Date.parse(left.publishedAt);
  const rightPublishedAt = Date.parse(right.publishedAt);
  if (Number.isFinite(leftPublishedAt) && Number.isFinite(rightPublishedAt) && leftPublishedAt !== rightPublishedAt) {
    return leftPublishedAt < rightPublishedAt ? -1 : 1;
  }
  return left.releaseId < right.releaseId ? -1 : 1;
}

function releaseComponent(releaseId: string): string | null {
  return RELEASE_ID_PATTERN.exec(releaseId)?.[3] ?? null;
}

function componentReleaseId(component: Release["web"]): string | null {
  const builtAt = new Date(component.builtAt);
  if (!Number.isFinite(builtAt.getTime())) return null;
  const timestamp = `${builtAt.toISOString().replace(/[-:]/g, "").slice(0, 13)}Z`;
  return `${timestamp}-${component.id}`;
}

/**
 * Full release identities can be ordered directly. Production clients currently carry only
 * `web.id`; an unknown non-current component cannot prove it meets a declared floor, so it is
 * conservatively treated as older.
 */
export function isWebReleaseBelowMinimum(
  localWebRelease: string,
  minimumWebRelease: string | null,
  currentWebRelease?: string
): boolean {
  if (minimumWebRelease === null || localWebRelease === currentWebRelease) return false;
  if (localWebRelease === minimumWebRelease || localWebRelease === releaseComponent(minimumWebRelease)) return false;
  if (releaseTimestamp(localWebRelease) !== null) {
    return compareReleaseIds(localWebRelease, minimumWebRelease) < 0;
  }
  return true;
}

function effectiveSeverity(release: Release, localWebRelease: string): ReleaseSeverity {
  return isWebReleaseBelowMinimum(localWebRelease, release.minimumWebRelease, release.web.id)
    ? "required"
    : release.severity;
}

function safeStorageRead(storage: SessionStorageLike | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeStorageWrite(storage: SessionStorageLike | null, key: string, value: string): boolean {
  try {
    storage?.setItem(key, value);
    return storage !== null;
  } catch {
    return false;
  }
}

function safeStorageRemove(storage: SessionStorageLike | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    // Consumption is best-effort when storage becomes unavailable between page loads.
  }
}

export function createReleaseUpdates(options: ReleaseUpdateOptions): ReleaseUpdates {
  const defaults = defaultRuntime();
  const runtime: ReleaseUpdateRuntime = { ...defaults, ...options.runtime };
  const countdownMs = options.countdownMs ?? REQUIRED_COUNTDOWN_MS;
  const snoozeMs = options.snoozeMs ?? RELEASE_SNOOZE_MS;
  const holds = new Map<symbol, string>();

  const state = $state({
    phase: "idle" as ReleaseUpdatePhase,
    release: null as Release | null,
    severity: null as ReleaseSeverity | null,
    source: null as ReleaseDetectionSource | null,
    countdownSeconds: null as number | null,
    holdReasons: [] as string[],
    snoozedUntil: null as number | null,
    trigger: null as ReleaseTrigger | null,
    detectedAt: null as number | null
  });

  let timer: ReturnType<typeof setTimeout> | null = null;
  let countdownRemainingMs = countdownMs;
  let countdownAdvancedAt: number | null = null;
  let knownLocalReleaseId = releaseTimestamp(options.localWebRelease) === null
    ? null
    : options.localWebRelease;

  function clearTimer(): void {
    if (timer === null) return;
    runtime.clearTimeout(timer);
    timer = null;
  }

  function emit(event: ReleaseUpdateEvent): void {
    options.onEvent?.(event);
  }

  function syncCountdown(): void {
    if (countdownAdvancedAt !== null) {
      const now = runtime.now();
      countdownRemainingMs = Math.max(0, countdownRemainingMs - Math.max(0, now - countdownAdvancedAt));
      countdownAdvancedAt = now;
    }
    state.countdownSeconds = state.severity === "required"
      ? Math.ceil(countdownRemainingMs / 1000)
      : null;
  }

  function writePostReloadMarker(): void {
    safeStorageWrite(runtime.sessionStorage, POST_RELOAD_KEY, JSON.stringify({
      releaseId: state.release?.releaseId ?? null
    }));
  }

  function performReload(): void {
    if (state.phase === "reloading") return;
    clearTimer();
    syncCountdown();
    writePostReloadMarker();
    state.phase = "reloading";
    const waitedSeconds = state.detectedAt === null
      ? 0
      : Math.max(0, Math.floor((runtime.now() - state.detectedAt) / 1000));
    emit({
      name: "release.update_applied",
      severity: state.severity ?? "required",
      trigger: state.trigger ?? "manual",
      waitedSeconds
    });
    runtime.reload();
  }

  function requestReload(trigger: ReleaseTrigger = "manual"): boolean {
    if (state.phase === "reloading" || state.release === null) return false;
    clearTimer();
    syncCountdown();
    state.trigger = trigger;
    if (holds.size > 0) {
      state.phase = "pending";
      return true;
    }
    performReload();
    return true;
  }

  function countdownTick(): void {
    timer = null;
    if (state.phase !== "available" || state.severity !== "required" || holds.size > 0) return;
    syncCountdown();
    if (countdownRemainingMs <= 0) {
      requestReload("countdown");
      return;
    }
    scheduleCountdown();
  }

  function scheduleCountdown(): void {
    clearTimer();
    if (state.phase !== "available" || state.severity !== "required" || holds.size > 0) return;
    countdownAdvancedAt = runtime.now();
    syncCountdown();
    timer = runtime.setTimeout(countdownTick, Math.min(1000, Math.max(0, countdownRemainingMs)));
  }

  function showSnoozedRelease(): void {
    timer = null;
    if (state.release === null || state.severity !== "notify" || state.snoozedUntil === null) return;
    const remaining = state.snoozedUntil - runtime.now();
    if (remaining > 0) {
      timer = runtime.setTimeout(showSnoozedRelease, remaining);
      return;
    }
    state.snoozedUntil = null;
    state.phase = "available";
  }

  function observe(release: Release, source: ReleaseDetectionSource): boolean {
    if (release.web.id === options.localWebRelease) {
      knownLocalReleaseId = componentReleaseId(release.web) ?? knownLocalReleaseId;
      return false;
    }

    const severity = effectiveSeverity(release, knownLocalReleaseId ?? options.localWebRelease);
    const prior = state.release;
    if (prior !== null) {
      const order = compareReleases(release, prior);
      if (order < 0 || (order === 0 && SEVERITY_RANK[severity] <= SEVERITY_RANK[state.severity ?? "silent"])) {
        return false;
      }
    }

    const reloadAlreadyPending = state.phase === "pending";
    const pendingTrigger = state.trigger;
    const pendingDetectedAt = state.detectedAt;
    clearTimer();
    state.release = release;
    state.severity = severity;
    state.source = source;
    state.trigger = reloadAlreadyPending ? pendingTrigger : null;
    state.detectedAt = reloadAlreadyPending ? pendingDetectedAt : runtime.now();
    state.snoozedUntil = null;
    countdownRemainingMs = countdownMs;
    countdownAdvancedAt = null;
    state.countdownSeconds = severity === "required" ? Math.ceil(countdownMs / 1000) : null;

    const publishedAt = Date.parse(release.publishedAt);
    emit({
      name: "release.update_detected",
      severity,
      source,
      ageSeconds: Number.isFinite(publishedAt)
        ? Math.max(0, Math.floor((runtime.now() - publishedAt) / 1000))
        : 0
    });

    if (reloadAlreadyPending) {
      state.phase = "pending";
    } else if (severity === "silent") {
      state.phase = "idle";
    } else {
      state.phase = "available";
      if (severity === "required") scheduleCountdown();
    }
    return true;
  }

  function snooze(): boolean {
    if (state.phase !== "available" || state.severity !== "notify") return false;
    clearTimer();
    state.snoozedUntil = runtime.now() + snoozeMs;
    state.phase = "idle";
    timer = runtime.setTimeout(showSnoozedRelease, snoozeMs);
    return true;
  }

  function hold(reason: string): () => void {
    const token = Symbol(reason);
    holds.set(token, reason);
    if (state.severity === "required" && state.phase === "available") {
      syncCountdown();
      clearTimer();
      countdownAdvancedAt = null;
    }
    state.holdReasons = [...holds.values()];

    let active = true;
    return () => {
      if (!active) return;
      active = false;
      holds.delete(token);
      state.holdReasons = [...holds.values()];
      if (holds.size > 0) return;
      if (state.phase === "pending") performReload();
      else if (state.phase === "available" && state.severity === "required") scheduleCountdown();
    };
  }

  function claimChunkReload(): boolean {
    if (safeStorageRead(runtime.sessionStorage, CHUNK_RELOAD_GUARD_KEY) !== null) return false;
    return safeStorageWrite(runtime.sessionStorage, CHUNK_RELOAD_GUARD_KEY, "1");
  }

  function consumePostReloadConfirmation(): PostReloadConfirmation | null {
    const marker = safeStorageRead(runtime.sessionStorage, POST_RELOAD_KEY);
    if (marker === null) return null;
    safeStorageRemove(runtime.sessionStorage, POST_RELOAD_KEY);
    try {
      const parsed = JSON.parse(marker) as { releaseId?: unknown };
      return {
        message: "Updated to the latest version.",
        releaseId: typeof parsed.releaseId === "string" ? parsed.releaseId : null
      };
    } catch {
      return { message: "Updated to the latest version.", releaseId: null };
    }
  }

  function dispose(): void {
    clearTimer();
  }

  return {
    get phase() { return state.phase; },
    get release() { return state.release; },
    get severity() { return state.severity; },
    get source() { return state.source; },
    get countdownSeconds() { return state.countdownSeconds; },
    get holdReasons() { return [...state.holdReasons]; },
    get held() { return state.holdReasons.length > 0; },
    get snoozedUntil() { return state.snoozedUntil; },
    get trigger() { return state.trigger; },
    observe,
    snooze,
    requestReload,
    hold,
    claimChunkReload,
    markPostReload: writePostReloadMarker,
    consumePostReloadConfirmation,
    dispose
  };
}

/** Shared per-tab store installed by the application wiring and update UI. */
export const updates = createReleaseUpdates({ localWebRelease: portalReleaseId, onEvent: track });
